import React, { useState, useEffect, useCallback } from 'react';
import { Package, DollarSign, Clock, Check, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const VendorCouponsTab = ({ t, showSuccessToast, showErrorToast }) => {
  const [balance, setBalance] = useState({ speedos: null, ryder: null });
  const [redeemedLog, setRedeemedLog] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentForm, setPaymentForm] = useState({ vendor: 'speedos', amount: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [logVendor, setLogVendor] = useState('');

  const fetchBalance = useCallback(async () => {
    try {
      const res = await axios.get('/api/vendor/admin/balance');
      setBalance(res.data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  }, []);

  const fetchRedeemedLog = useCallback(async () => {
    try {
      const params = logVendor ? `?vendor=${logVendor}` : '';
      const res = await axios.get(`/api/vendor/admin/redeemed-log${params}`);
      setRedeemedLog(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching log:', error);
    }
  }, [logVendor]);

  const fetchPayments = useCallback(async () => {
    try {
      const res = await axios.get('/api/vendor/admin/payments');
      setPayments(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBalance(), fetchRedeemedLog(), fetchPayments()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBalance, fetchRedeemedLog, fetchPayments]);

  useEffect(() => {
    if (showLog) fetchRedeemedLog();
  }, [logVendor, showLog, fetchRedeemedLog]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post('/api/vendor/admin/payment', {
        vendor: paymentForm.vendor,
        amount: parseFloat(paymentForm.amount),
        note: paymentForm.note
      });
      toast.success(`Payment of ${paymentForm.amount} ILS recorded for ${paymentForm.vendor}`);
      setPaymentForm({ ...paymentForm, amount: '', note: '' });
      fetchBalance();
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
        <RefreshCw className="animate-spin" style={{ margin: '0 auto 10px' }} />
        Loading...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
          <Package size={20} style={{ color: '#a78bfa' }} />
          Vendor Payments
        </h2>
        <button
          onClick={() => { fetchBalance(); fetchRedeemedLog(); fetchPayments(); }}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#374151', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Balance Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {['speedos', 'ryder'].map(vendor => (
          <div key={vendor} style={{ background: '#1f2937', borderRadius: '10px', padding: '16px', border: '1px solid #374151' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ color: '#a78bfa', fontWeight: '600', fontSize: '16px', textTransform: 'capitalize' }}>{vendor}</span>
              <span style={{ color: '#6b7280', fontSize: '12px' }}>{balance[vendor]?.redeemed || 0} redeemed</span>
            </div>
            <div style={{ display: 'flex', gap: '20px' }}>
              <div>
                <div style={{ color: '#f59e0b', fontSize: '22px', fontWeight: '700' }}>{balance[vendor]?.owed || 0} ILS</div>
                <div style={{ color: '#9ca3af', fontSize: '12px' }}>Owed</div>
              </div>
              <div>
                <div style={{ color: '#34d399', fontSize: '22px', fontWeight: '700' }}>{balance[vendor]?.paid || 0} ILS</div>
                <div style={{ color: '#9ca3af', fontSize: '12px' }}>Paid</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Record Payment Form */}
      <div style={{ background: '#111827', borderRadius: '10px', padding: '16px', marginBottom: '16px', border: '1px solid #374151' }}>
        <h3 style={{ color: '#fff', fontSize: '14px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <DollarSign size={16} style={{ color: '#34d399' }} /> Record Payment
        </h3>
        <form onSubmit={handlePayment} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Vendor</label>
            <select
              value={paymentForm.vendor}
              onChange={(e) => setPaymentForm({ ...paymentForm, vendor: e.target.value })}
              style={{ padding: '8px 12px', background: '#1f2937', border: '1px solid #374151', borderRadius: '6px', color: '#fff', minWidth: '100px' }}
            >
              <option value="speedos">Speedos</option>
              <option value="ryder">Ryder</option>
            </select>
          </div>
          <div>
            <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Amount (ILS)</label>
            <input
              type="number"
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              placeholder="0"
              style={{ padding: '8px 12px', background: '#1f2937', border: '1px solid #374151', borderRadius: '6px', color: '#fff', width: '100px' }}
            />
          </div>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={{ color: '#9ca3af', fontSize: '11px', display: 'block', marginBottom: '4px' }}>Note (optional)</label>
            <input
              type="text"
              value={paymentForm.note}
              onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
              placeholder="e.g., Bank transfer"
              style={{ padding: '8px 12px', background: '#1f2937', border: '1px solid #374151', borderRadius: '6px', color: '#fff', width: '100%' }}
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            style={{ padding: '8px 20px', background: '#059669', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', fontWeight: '500' }}
          >
            {submitting ? 'Recording...' : 'Record Payment'}
          </button>
        </form>
      </div>

      {/* Payment History Toggle */}
      <button
        onClick={() => setShowPayments(!showPayments)}
        style={{ width: '100%', padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#9ca3af', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Check size={14} /> Payment History ({payments.length})
        </span>
        {showPayments ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showPayments && payments.length > 0 && (
        <div style={{ background: '#111827', borderRadius: '8px', padding: '12px', marginBottom: '16px', maxHeight: '200px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Date</th>
                <th style={{ padding: '6px 8px' }}>Vendor</th>
                <th style={{ padding: '6px 8px' }}>Amount</th>
                <th style={{ padding: '6px 8px' }}>Note</th>
                <th style={{ padding: '6px 8px' }}>By</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(p => (
                <tr key={p._id} style={{ color: '#d1d5db', borderTop: '1px solid #374151' }}>
                  <td style={{ padding: '6px 8px' }}>{formatDate(p.createdAt)}</td>
                  <td style={{ padding: '6px 8px', textTransform: 'capitalize' }}>{p.vendor}</td>
                  <td style={{ padding: '6px 8px', color: '#34d399' }}>{p.amount} ILS</td>
                  <td style={{ padding: '6px 8px', color: '#9ca3af' }}>{p.note || '-'}</td>
                  <td style={{ padding: '6px 8px' }}>{p.recordedBy?.username || 'Unknown'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Redeemed Log Toggle */}
      <button
        onClick={() => setShowLog(!showLog)}
        style={{ width: '100%', padding: '10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '8px', color: '#9ca3af', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Clock size={14} /> Redeemed Coupons Log ({redeemedLog.length})
        </span>
        {showLog ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showLog && (
        <div style={{ background: '#111827', borderRadius: '8px', padding: '12px' }}>
          <div style={{ marginBottom: '10px' }}>
            <select
              value={logVendor}
              onChange={(e) => setLogVendor(e.target.value)}
              style={{ padding: '6px 10px', background: '#1f2937', border: '1px solid #374151', borderRadius: '4px', color: '#fff', fontSize: '12px' }}
            >
              <option value="">All Vendors</option>
              <option value="speedos">Speedos</option>
              <option value="ryder">Ryder</option>
            </select>
          </div>
          {redeemedLog.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>No redeemed coupons</div>
          ) : (
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px' }}>User</th>
                    <th style={{ padding: '6px 8px' }}>Vendor</th>
                    <th style={{ padding: '6px 8px' }}>Package</th>
                    <th style={{ padding: '6px 8px' }}>ILS</th>
                    <th style={{ padding: "6px 8px" }}>Code</th>
                    <th style={{ padding: "6px 8px" }}>Redeemed</th>
                  </tr>
                </thead>
                <tbody>
                  {redeemedLog.map(c => (
                    <tr key={c._id} style={{ color: '#d1d5db', borderTop: '1px solid #374151' }}>
                      <td style={{ padding: '6px 8px' }}>{c.username || 'Unknown'}</td>
                      <td style={{ padding: '6px 8px', textTransform: 'capitalize' }}>{c.vendor}</td>
                      <td style={{ padding: '6px 8px' }}>{c.packageCategory}</td>
                      <td style={{ padding: '6px 8px', color: '#60a5fa' }}>{c.priceILS}</td>
                      <td style={{ padding: "6px 8px", color: "#a78bfa", fontFamily: "monospace" }}>{c.couponCode || "-"}</td>
                      <td style={{ padding: "6px 8px", color: "#9ca3af" }}>{formatDate(c.redeemedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorCouponsTab;
